import React from 'react';
import useSteps from '../../hooks/useSteps';
import FormWizard from '../../components/FormWizard/FormWizard';
import SwipeTransition from '../../components/SwipeTransition/SwipeTransition';
import useStateWithInputEventHandler from '../../hooks/useStateWithInputEventHandler';
import MobileInput from './components/MobileInput/MobileInput';
import CodeInput from './components/CodeInput/CodeInput';
import { replaceUrlParamsWithValue, setUserDataToStorage } from '../../utils';
import useBackendApi from '../../hooks/useBackendApi';
import useClearableState from '../../hooks/useClearableState';
import ErrorModal from '../../lib/ErrorModal/ErrorModal';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Input } from '../../flayr-components';

interface ILoginWizardProps {
  loginWith?: 'mobile' | 'email';
  open?: boolean;
  drawerView?: boolean;
  onClose?: (e?: any) => void;
  onFinish?: (e?: any) => void;
}

const LoginWizard: React.FC<React.PropsWithChildren<React.PropsWithChildren<ILoginWizardProps>>> = ({
  loginWith = 'mobile',
  open,
  onClose,
  onFinish,
  drawerView,
}) => {
  const setpsHeadingsMapping = {
    email: {
      title: `What's your Email address?`,
      subTitle: 'Login',
    },
    mobile: {
      title: `What's your mobile number?`,
      subTitle: 'Login',
    },
    code: {
      subTitle: 'Verification Code',
      title: `Enter code ${
        (loginWith === 'mobile' && 'texted to your mobile') || ''
      }${(loginWith === 'email' && 'emailed to you') || ''}`,
    },
  };
  const { step, steps, setSteps, isLastStep, isFirstStep, index, prev, next } =
    useSteps(['mobile', 'code']);

  const { get, post } = useBackendApi();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [err, setErr, clearErr] = useClearableState('');

  const [input, handleChange, setInput] = useStateWithInputEventHandler({
    mobile: '',
    code: '',
    email: '',
  });
  const { mobile, code, email } = input;

  React.useEffect(() => {
    const steps = ['code'];
    steps.unshift(loginWith);
    setSteps(steps);
  }, [loginWith]);

  function handleBack() {
    if (isFirstStep) {
      return onClose?.();
    }
    prev();
  }
  function handleNext() {
    switch (step) {
      case 'mobile':
      case 'email':
        return submitMobileOrEmail();
      case 'code':
        if (code.length === 4) {
          return submitCode();
        }
        return;
    }
  }

  function handleClear() {
    setInput({
      ...input,
      [step]: '',
    });
  }

  async function submitMobileOrEmail() {
    setLoading(true);
    let url: string = '';
    switch (loginWith) {
      case 'mobile':
        url = replaceUrlParamsWithValue('/v1/users/mobileLogin/:mobile', {
          mobile,
        });
        break;
      case 'email':
        url = replaceUrlParamsWithValue('/v1/users/emailLogin/:email', {
          email,
        });
        break;
    }
    await get(
      url,
      {},
      {
        loader: false,
        onError: (err) => {
          setErr(err.message);
          setLoading(false);
        },
      }
    );
    setLoading(false);
    if (['mobile', 'email'].includes(step)) {
      next();
    }
  }

  async function submitCode(e?) {
    const data = e || code;

    setLoading(true);
    let url = '';
    switch (loginWith) {
      case 'mobile':
        url = replaceUrlParamsWithValue('/v1/users/mobileLogin/:mobile/:code', {
          mobile,
          code: data,
        });
        break;
      case 'email':
        url = replaceUrlParamsWithValue('/v1/users/emailLogin/:email/:code', {
          email,
          code: data,
        });
        break;
    }

    const res = await post(
      url,
      {},
      {
        loader: false,
        onError: (err) => {
          setErr(err.message);
          setLoading(false);
        },
      }
    );
    setLoading(false);
    setUserDataToStorage({ token: res.token });
    onFinish?.(res.data.user);
    onClose?.();
  }

  const isFormValid = React.useMemo(() => {
    switch (step) {
      case 'mobile':
        return mobile && isValidPhoneNumber(mobile);
      case 'code':
        return code.length === 4;
    }
    return true;
  }, []);

  return (
    <FormWizard
      open={open}
      onClose={onClose}
      onBack={handleBack}
      title={setpsHeadingsMapping[step]?.title}
      subTitle={setpsHeadingsMapping[step]?.subTitle}
      onNext={handleNext}
      nextBtnDisabled={!isFormValid}
      nextBtnLabel={(isLastStep && 'Done') || 'Next'}
      onClear={handleClear}
      loading={loading}
      drawerView={drawerView}
    >
      <SwipeTransition activeStepIndex={index} duration="500">
        {loginWith === 'mobile' && (
          <MobileInput value={mobile} onChange={handleChange} />
        )}
        {loginWith === 'email' && (
          <Input
            type="email"
            name="email"
            value={email}
            onChange={handleChange}
            size="lg"
            placeholder="Enter email"
            validation={['required', 'email']}
            className="text-gray-700"
          />
        )}
        <CodeInput
          value={code}
          onChange={handleChange}
          onResendCode={submitMobileOrEmail}
          onComplete={submitCode}
        />
      </SwipeTransition>
      <div className="text-gray-700">
        <ErrorModal open={!!err} message={err} onClose={clearErr} size="lg" />
      </div>
    </FormWizard>
  );
};
export default LoginWizard;
